import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { LogOut, CalendarClock, ArrowUpRight, KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/components/Nav";
import { content } from "@/data/content";

export default function Members() {
  const { user, loading, login, logout } = useAuth();

  useEffect(() => {
    if (!loading && !user) login();
  }, [loading, user, login]);

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#1A3B2A] text-[#F7F7F4]">
        <p className="font-display text-3xl">Laddar…</p>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#1A3B2A] text-[#F7F7F4] bp-grain relative overflow-hidden">
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#4A6D56]/40 blur-3xl" />
      <header className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-full bg-white overflow-hidden">
            <img src={LOGO_URL} alt="Sundsvalls Bilpolare logotyp" className="h-8 w-8 object-contain" />
          </span>
          <span className="font-display text-2xl">{content.brand}</span>
        </a>
        <div className="flex items-center gap-4">
          {user.is_admin && (
            <a href="/admin" data-testid="members-admin-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C2D3C4]">
              <ShieldCheck size={16} /> Admin
            </a>
          )}
          {user.picture && (
            <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full object-cover border border-white/30" />
          )}
          <button
            data-testid="members-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-sm font-semibold hover:bg-white/10 transition-colors duration-300"
          >
            <LogOut size={16} /> Logga ut
          </button>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-14 sm:pt-24 pb-24">
        <motion.p
          data-testid="members-welcome"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-[#C2D3C4] uppercase tracking-[0.25em] text-sm font-bold"
        >
          Inloggad som {user.name}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display text-5xl sm:text-7xl font-light mt-5 leading-tight"
        >
          Mina sidor
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-12 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 p-10 sm:p-14 max-w-3xl"
        >
          <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-[#C2D3C4] text-[#1A3B2A] mb-6">
            <CalendarClock size={26} />
          </span>
          <h2 className="font-display text-3xl sm:text-4xl">Bokningssystemet kommer snart</h2>
          <p className="text-[#F7F7F4]/75 mt-4 leading-relaxed max-w-xl">
            Vi bygger just nu ett nytt bokningssystem direkt här på sidan. Tills det är på plats
            bokar du bilen som vanligt via BokaBil.se – nycklar och bränslekort finns i garaget.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="https://www.bokabil.se"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="members-bokabil-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#F7F7F4] text-[#1A3B2A] px-6 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-[#C2D3C4] active:scale-95 transition-[background-color,transform] duration-300"
            >
              Boka via BokaBil.se <ArrowUpRight size={17} />
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3.5 text-sm font-semibold hover:bg-white/10 transition-colors duration-300"
            >
              Till startsidan
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-6 flex items-center gap-3 text-[#F7F7F4]/50 text-sm"
        >
          <KeyRound size={16} /> Din session är säker och gäller i 7 dagar.
        </motion.div>
      </section>
    </main>
  );
}
